import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import PlaylistIcon from "@mui/icons-material/QueueMusicOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { tokens } from "../theme";
import type { SignageDayPlan, SignagePlaylist } from "@shared/types/signage";

/**
 * Choose what a weekday plays.
 *
 * Two kinds of answer, and the split is the whole point:
 *
 *  - a SCHEDULE (a day plan) - "morning news, then the lunch loop, then the bar
 *    reel" - built in the playlist builder;
 *  - a single PLAYLIST looped all day, which is what most days actually want.
 *
 * The second is offered directly so nobody has to open the builder, name a
 * thing, and drag one block across 24 hours just to say "play this". Picking it
 * creates (or reuses) a day plan holding one 00:00-24:00 block behind the
 * scenes - the operator never meets the concept.
 */

export interface SignageDayPlanPickerProps {
  open: boolean;
  onClose: () => void;
  dayLabel: string;
  dayPlans: SignageDayPlan[];
  playlists: SignagePlaylist[];
  /** Pick an existing multi-block schedule. */
  onPickPlan: (planId: string) => void;
  /** Loop one playlist for the whole day. */
  onPickAllDay: (playlist: SignagePlaylist) => void;
  busy?: boolean;
}

function Row({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: `1px solid ${tokens.divider}`,
        borderRadius: "10px",
        bgcolor: tokens.surface,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        "&:hover": disabled ? undefined : { borderColor: tokens.brand },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "8px",
          bgcolor: tokens.brandTintSoft,
          display: "grid",
          placeItems: "center",
          color: tokens.brand,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: 15, fontWeight: 600, color: tokens.ink }}
        >
          {title}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12, color: tokens.inkMuted }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

/** "Loops all day", "09:00 – 17:00", or "3 blocks · 06:00 – 23:00". */
export function describePlan(plan: SignageDayPlan): string {
  const blocks = [...plan.blocks].sort((a, b) =>
    a.start.localeCompare(b.start)
  );
  if (blocks.length === 0) return "Empty";
  const first = blocks[0]!;
  const last = blocks[blocks.length - 1]!;
  if (
    blocks.length === 1 &&
    first.start === "00:00" &&
    (first.end === "24:00" || first.end === "00:00")
  ) {
    return "Loops all day";
  }
  if (blocks.length === 1) return `${first.start} – ${first.end}`;
  return `${blocks.length} blocks · ${first.start} – ${last.end}`;
}

export default function SignageDayPlanPicker({
  open,
  onClose,
  dayLabel,
  dayPlans,
  playlists,
  onPickPlan,
  onPickAllDay,
  busy,
}: SignageDayPlanPickerProps) {
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const plans = useMemo(
    () => dayPlans.filter((p) => !q || p.name.toLowerCase().includes(q)),
    [dayPlans, q]
  );
  const loops = useMemo(
    () => playlists.filter((p) => !q || p.name.toLowerCase().includes(q)),
    [playlists, q]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: tokens.ink }}>
          What plays on {dayLabel}?
        </Typography>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mb: 2 }}>
          Pick a schedule, or loop a single playlist for the whole day.
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {plans.length > 0 && (
          <>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: tokens.inkMuted,
                mb: 1,
              }}
            >
              SCHEDULES
            </Typography>
            <Stack gap={1} sx={{ mb: 2.5 }}>
              {plans.map((p) => (
                <Row
                  key={p.id}
                  icon={<ScheduleIcon />}
                  title={p.name}
                  subtitle={describePlan(p)}
                  disabled={busy}
                  onClick={() => onPickPlan(p.id)}
                />
              ))}
            </Stack>
          </>
        )}

        <Typography
          sx={{ fontSize: 12, fontWeight: 700, color: tokens.inkMuted, mb: 1 }}
        >
          LOOP ALL DAY
        </Typography>
        {loops.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
            No playlists here yet. Build one on the Playlists tab first.
          </Typography>
        ) : (
          <Stack gap={1}>
            {loops.map((p) => (
              <Row
                key={p.id}
                icon={<LoopIcon />}
                title={p.name}
                subtitle={`${p.items.length} ${
                  p.items.length === 1 ? "item" : "items"
                } · loops all day`}
                disabled={busy}
                onClick={() => onPickAllDay(p)}
              />
            ))}
          </Stack>
        )}

        {plans.length === 0 && loops.length === 0 && (
          <Stack alignItems="center" sx={{ py: 3 }} gap={0.5}>
            <PlaylistIcon sx={{ color: tokens.inkFaint }} />
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Nothing to schedule yet. Create a playlist first.
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button onClick={onClose}>Cancel</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
